import type { CharacterBenefits } from '../../../hooks/useCharacterBenefits';
import type { PlaySession } from '../../../stores/playSessionStore';
import type { CharacterDraft } from '../../../stores/characterCreationStore';
import type { EquipmentData, SpellLists } from './types';
import { getSpell } from '@character-creator/data';
import { CollapsibleSection } from '../../ui/CollapsibleSection';

interface BelowFoldSectionsProps {
  benefits: CharacterBenefits;
  session: PlaySession | null;
  draft: CharacterDraft;
  equipment: EquipmentData;
  spells: SpellLists;
  expandedSections: Set<string>;
  onToggleSection: (section: string) => void;
  onRollAttack: (label: string, modifier: number) => void;
  onRollDamage: (formula: string, label: string) => void;
  onUseSpellSlot: (level: string) => void;
  onSetConcentration: (spell: string | null) => void;
}

export function BelowFoldSections({
  benefits,
  session,
  draft,
  equipment,
  spells,
  expandedSections,
  onToggleSection,
  onRollAttack,
  onRollDamage,
  onUseSpellSlot,
  onSetConcentration,
}: BelowFoldSectionsProps) {
  return (
    <div className="lg:col-span-full mt-6 pt-6 border-t border-background-elevated">
      <div className="text-center text-[10px] text-text-muted uppercase tracking-widest mb-4">
        Full Character Details
      </div>

      {/* Responsive grid: 1 col mobile, 2 col lg, 3 col xl */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 auto-rows-min">

        {/* Character Info Section */}
        <CollapsibleSection
          title="Character Info"
          badge={`${draft.raceId ? draft.raceId.charAt(0).toUpperCase() + draft.raceId.slice(1) : ''} ${benefits.classLevels.map(c => c.className).join('/')} ${benefits.characterLevel}`}
          isExpanded={expandedSections.has('info')}
          onToggle={() => onToggleSection('info')}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Identity */}
            <div className="space-y-2">
              <div className="text-xs text-text-muted uppercase">Identity</div>
              <div className="text-text-primary font-display text-lg">{draft.name || 'Unnamed'}</div>
              <div className="text-sm text-text-secondary">
                Level {benefits.characterLevel} {benefits.classLevels.map(c => `${c.className} ${c.level}`).join(' / ')}
              </div>
              {draft.raceId && (
                <div className="text-sm text-text-secondary">
                  {draft.raceId.charAt(0).toUpperCase() + draft.raceId.slice(1)}
                  {draft.subraceId && ` (${draft.subraceId})`}
                </div>
              )}
              {draft.alignment && (
                <div className="text-sm text-text-muted">{draft.alignment}</div>
              )}
            </div>

            {/* Background */}
            {draft.backgroundId && (
              <div className="space-y-2">
                <div className="text-xs text-text-muted uppercase">Background</div>
                <div className="text-text-primary">{draft.backgroundId.charAt(0).toUpperCase() + draft.backgroundId.slice(1)}</div>
              </div>
            )}

            {/* Appearance */}
            {draft.appearance && Object.values(draft.appearance).some(v => v) && (
              <div className="md:col-span-2 space-y-2">
                <div className="text-xs text-text-muted uppercase">Appearance</div>
                <div className="flex flex-wrap gap-2">
                  {draft.appearance.age && (
                    <span className="px-2 py-1 bg-background-elevated rounded text-xs">
                      <span className="text-text-muted">Age:</span> {draft.appearance.age}
                    </span>
                  )}
                  {draft.appearance.height && (
                    <span className="px-2 py-1 bg-background-elevated rounded text-xs">
                      <span className="text-text-muted">Height:</span> {draft.appearance.height}
                    </span>
                  )}
                  {draft.appearance.weight && (
                    <span className="px-2 py-1 bg-background-elevated rounded text-xs">
                      <span className="text-text-muted">Weight:</span> {draft.appearance.weight}
                    </span>
                  )}
                  {draft.appearance.eyes && (
                    <span className="px-2 py-1 bg-background-elevated rounded text-xs">
                      <span className="text-text-muted">Eyes:</span> {draft.appearance.eyes}
                    </span>
                  )}
                  {draft.appearance.hair && (
                    <span className="px-2 py-1 bg-background-elevated rounded text-xs">
                      <span className="text-text-muted">Hair:</span> {draft.appearance.hair}
                    </span>
                  )}
                  {draft.appearance.skin && (
                    <span className="px-2 py-1 bg-background-elevated rounded text-xs">
                      <span className="text-text-muted">Skin:</span> {draft.appearance.skin}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Personality & Background Section */}
        {(draft.personalityTraits.length > 0 || draft.bonds.length > 0 || draft.ideals.length > 0 || draft.flaws.length > 0 || draft.backstory) && (
          <CollapsibleSection
            title="Personality & Background"
            isExpanded={expandedSections.has('personality')}
            onToggle={() => onToggleSection('personality')}
          >
            <div className="space-y-4">
              {draft.personalityTraits.length > 0 && (
                <div>
                  <div className="text-xs text-text-muted uppercase mb-1">Personality Traits</div>
                  <p className="text-sm text-text-secondary italic">{draft.personalityTraits.join('. ')}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {draft.bonds.length > 0 && (
                  <div className="p-3 bg-background-elevated rounded">
                    <div className="text-xs text-class-wizard uppercase mb-1">Bond</div>
                    <p className="text-sm text-text-secondary">{draft.bonds.join('. ')}</p>
                  </div>
                )}
                {draft.ideals.length > 0 && (
                  <div className="p-3 bg-background-elevated rounded">
                    <div className="text-xs text-accent-gold uppercase mb-1">Ideal</div>
                    <p className="text-sm text-text-secondary">{draft.ideals.join('. ')}</p>
                  </div>
                )}
                {draft.flaws.length > 0 && (
                  <div className="p-3 bg-background-elevated rounded">
                    <div className="text-xs text-error uppercase mb-1">Flaw</div>
                    <p className="text-sm text-text-secondary">{draft.flaws.join('. ')}</p>
                  </div>
                )}
              </div>
              {draft.backstory && (
                <div>
                  <div className="text-xs text-text-muted uppercase mb-1">Backstory</div>
                  <div className="text-sm text-text-secondary leading-relaxed space-y-2">
                    {draft.backstory.split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Attacks Section */}
        {equipment.attacks.length > 0 && (
          <CollapsibleSection
            title="Attacks"
            badge={`${equipment.attacks.length} weapons`}
            isExpanded={expandedSections.has('attacks')}
            onToggle={() => onToggleSection('attacks')}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {equipment.attacks.map((attack, index) => {
                const bonusNum = parseInt(attack.bonus.replace('+', ''), 10) || 0;
                return (
                  <div key={index} className="p-3 bg-background-elevated rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-display text-sm text-text-primary">{attack.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-background-primary rounded text-text-muted">
                        {attack.damageType}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onRollAttack(`${attack.name} Attack`, bonusNum)}
                        className="flex-1 p-2 bg-background-card rounded text-center
                          hover:bg-accent-bronze/20 transition-colors active:scale-[0.98]"
                      >
                        <div className="text-[10px] text-text-muted">To Hit</div>
                        <div className="font-mono text-lg font-bold text-accent-gold">{attack.bonus}</div>
                      </button>
                      <button
                        onClick={() => onRollDamage(attack.damage, `${attack.name} Damage`)}
                        className="flex-1 p-2 bg-background-card rounded text-center
                          hover:bg-error/20 transition-colors active:scale-[0.98]"
                      >
                        <div className="text-[10px] text-text-muted">Damage</div>
                        <div className="font-mono text-sm font-bold text-error">{attack.damage}</div>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* Spells Section - spans 2 cols on lg+ for better spell layout */}
        {(benefits.spellcasting && (spells.cantrips.length > 0 || spells.knownSpells.length > 0 || spells.preparedSpells.length > 0)) || (benefits.racialSpellcasting && benefits.racialSpellcasting.length > 0) ? (
          <CollapsibleSection
            title="Spells"
            badge={benefits.spellcasting ? `DC ${benefits.spellcasting.saveDC} • +${benefits.spellcasting.attackBonus}` : 'Innate'}
            isExpanded={expandedSections.has('spells')}
            onToggle={() => onToggleSection('spells')}
            span={2}
          >
            <div className="space-y-4">
              {/* Concentration indicator */}
              {session?.concentrationSpell && (
                <div className="p-2 bg-class-wizard/20 border border-class-wizard/50 rounded flex justify-between items-center">
                  <div>
                    <div className="text-[10px] text-class-wizard uppercase">Concentrating</div>
                    <div className="text-sm text-text-primary">{session.concentrationSpell}</div>
                  </div>
                  <button
                    onClick={() => onSetConcentration(null)}
                    className="px-2 py-1 text-xs border border-error text-error rounded hover:bg-error hover:text-white transition-colors"
                  >
                    Drop
                  </button>
                </div>
              )}

              {/* Racial Spells */}
              {benefits.racialSpellcasting && benefits.racialSpellcasting.length > 0 && (
                <>
                  {benefits.racialSpellcasting.map((info) => (
                    <div key={info.sourceTrait}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-xs text-text-muted uppercase">{info.sourceTrait}</div>
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-accent-purple/20 text-accent-purple rounded">
                          Innate
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {info.cantrips.map((spellId) => {
                          const spell = getSpell(spellId);
                          return (
                            <span
                              key={spellId}
                              className="px-2 py-1 bg-background-elevated rounded text-xs text-text-secondary flex items-center gap-1"
                            >
                              {spell?.name ?? spellId}
                              <span className="text-accent-purple text-[10px]">At Will</span>
                            </span>
                          );
                        })}
                        {info.spells.map((spell) => (
                          <span
                            key={spell.spellId}
                            className="px-2 py-1 bg-background-elevated rounded text-xs text-text-secondary flex items-center gap-1"
                          >
                            {spell.spellName}
                            {spell.usesPerRest && (
                              <span className="text-accent-purple text-[10px]">
                                {spell.usesPerRest}/{spell.restType === 'long' ? 'LR' : 'SR'}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Class Spell list grouped by level */}
              {(() => {
                const allSpellIds = [...new Set([...spells.cantrips, ...spells.knownSpells, ...spells.preparedSpells])];
                const spellsByLevel = new Map<number, Array<{ id: string; name: string; school: string; concentration: boolean }>>();

                allSpellIds.forEach((spellId) => {
                  const spell = getSpell(spellId);
                  if (spell) {
                    const level = spell.level;
                    if (!spellsByLevel.has(level)) spellsByLevel.set(level, []);
                    spellsByLevel.get(level)!.push({
                      id: spell.id,
                      name: spell.name,
                      school: spell.school,
                      concentration: spell.concentration,
                    });
                  }
                });

                const sortedLevels = Array.from(spellsByLevel.keys()).sort((a, b) => a - b);

                return sortedLevels.map((level) => (
                  <div key={level}>
                    <div className="text-xs text-text-muted uppercase mb-2">
                      {level === 0 ? 'Cantrips' : `Level ${level}`}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {spellsByLevel.get(level)!.map((spell) => (
                        <button
                          key={spell.id}
                          onClick={() => {
                            if (level > 0) onUseSpellSlot(level.toString());
                            if (spell.concentration) onSetConcentration(spell.name);
                          }}
                          className="px-2 py-1 bg-background-elevated rounded text-xs text-text-secondary
                            hover:bg-class-wizard/20 transition-colors flex items-center gap-1"
                        >
                          {spell.name}
                          {spell.concentration && <span className="text-class-wizard">C</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </CollapsibleSection>
        ) : null}

        {/* Racial Traits Section */}
        {benefits.racialTraits.length > 0 && (
          <CollapsibleSection
            title="Racial Traits"
            badge={benefits.racialTraits.length}
            isExpanded={expandedSections.has('racial')}
            onToggle={() => onToggleSection('racial')}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {benefits.racialTraits.map((trait) => (
                <div key={trait.id} className="p-3 bg-background-elevated rounded
                  hover:bg-background-elevated/80 transition-colors">
                  <div className="font-display text-sm text-text-primary mb-1">{trait.name}</div>
                  <p className="text-xs text-text-secondary leading-relaxed line-clamp-3">{trait.description}</p>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Feats Section */}
        {benefits.feats.length > 0 && (
          <CollapsibleSection
            title="Feats"
            badge={benefits.feats.length}
            isExpanded={expandedSections.has('feats')}
            onToggle={() => onToggleSection('feats')}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {benefits.feats.map((feat) => (
                <div key={feat.id} className="p-3 bg-background-elevated rounded
                  hover:bg-background-elevated/80 transition-colors">
                  <div className="font-display text-sm text-text-primary mb-1">{feat.name}</div>
                  <p className="text-xs text-text-secondary leading-relaxed mb-2 line-clamp-2">{feat.description}</p>
                  {feat.benefits.length > 0 && (
                    <ul className="text-xs text-text-muted list-disc list-inside space-y-0.5">
                      {feat.benefits.slice(0, 3).map((b, i) => <li key={i} className="line-clamp-1">{b}</li>)}
                      {feat.benefits.length > 3 && (
                        <li className="text-accent-gold">+{feat.benefits.length - 3} more...</li>
                      )}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

      </div>{/* Close responsive grid */}
    </div>
  );
}
